import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../store/slices/authSlice";
import api from "../utils/axios";
import AIPackageBuilder from "../components/vendor/AIPackageBuilder";
import AIDocumentReader from "../components/vendor/AIDocumentReader";
import AIBusinessVerifier from "../components/vendor/AIBusinessVerifier";
import LeadScoreBoard from "../components/vendor/LeadScoreBoard";
import AutoReplySettings from "../components/vendor/AutoReplySettings";
import CalendarBlockManager from "../components/vendor/CalendarBlockManager";
import AIProfileScoreCard from "../components/vendor/AIProfileScoreCard";
import {
  PenTool, FileText, ShieldCheck, Flame, MessageSquare, Calendar,
  Package, Lightbulb, ArrowRight, Sparkles,
} from "lucide-react";
import { SectionSkeleton } from "../components/UI/Skeleton";
import toast from "react-hot-toast";

/* ── Feature cards shown on the overview grid ─────────────────────────────── */
const FEATURES = [
  {
    key: "leads",
    icon: Flame,
    label: "Lead Scoring",
    desc: "AI ranks every lead so you respond to hot ones first",
    color: "red",
    gradient: "from-pink-500 to-pink-500",
  },
  {
    key: "packages",
    icon: Package,
    label: "Package Builder",
    desc: "Auto-generate pricing packages & analyse rate cards",
    color: "purple",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    key: "autoreply",
    icon: MessageSquare,
    label: "Auto-Reply",
    desc: "Never miss a lead — AI replies when you're offline",
    color: "indigo",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    key: "calendar",
    icon: Calendar,
    label: "Calendar",
    desc: "Block dates, prevent double-bookings automatically",
    color: "blue",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    key: "documents",
    icon: FileText,
    label: "Document Reader",
    desc: "Upload GSTIN / PAN / license — AI extracts the data",
    color: "emerald",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    key: "verification",
    icon: ShieldCheck,
    label: "Verification",
    desc: "Instantly verify GSTIN, bank account & PAN number",
    color: "amber",
    gradient: "from-purple-500 to-pink-500",
  },
];

/* ── Sidebar tab items (when a feature is opened) ─────────────────────────── */
const TABS = [
  { key: "overview",      icon: Sparkles,          label: "Overview" },
  { key: "leads",         icon: Flame,       label: "Lead Scoring" },
  { key: "packages",      icon: Package,     label: "Package Builder" },
  { key: "autoreply",     icon: MessageSquare,         label: "Auto-Reply" },
  { key: "calendar",      icon: Calendar,    label: "Calendar" },
  { key: "documents",     icon: FileText,    label: "Doc Reader" },
  { key: "verification",  icon: ShieldCheck, label: "Verification" },
];

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function AIEngine() {
  const user = useSelector(selectUser);
  const vendor = user?.vendor || user;
  const [activeTab, setActiveTab] = useState("overview");
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/api/v1/services")
      .then(({ data }) => {
        const raw = data?.data?.services || data?.data?.list || data?.data || [];
        const list = Array.isArray(raw) ? raw : Object.values(raw).flat();
        setServices(list);
        if (list.length > 0) setSelectedService(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const venues = services.filter(
    (s) => s.serviceType === "venue" || s.subCategories
  );

  /* ── Loading state ─────────────────────────────────────────────────────── */
  if (loading) {
    return <SectionSkeleton cards={6} />;
  }

  /* ── Empty state ───────────────────────────────────────────────────────── */
  if (!loading && services.length === 0) {
    return (
      <div className="space-y-5">
        <Header />
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl px-6 py-14 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50">
            <PenTool className="w-6 h-6 text-purple-500" />
          </div>
          <h3 className="font-['Manrope'] text-lg font-bold text-gray-900 mb-1">No services yet</h3>
          <p className="text-sm text-gray-500">
            Add your first service from the <span className="font-semibold text-gray-900">Services</span> tab to unlock the AI Engine
          </p>
        </div>
      </div>
    );
  }

  /* ── Main layout ───────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">
      <Header toolCount={FEATURES.length} serviceCount={services.length} />

      {/* Horizontal tabs (scroll on mobile) */}
      <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto p-1 rounded-xl bg-white border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] scrollbar-none">
        {TABS.map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-150
              ${activeTab === key
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"}
            `}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Service selector + Profile score — shown for service-specific tabs */}
      {["packages", "documents", "verification"].includes(activeTab) && selectedService && (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-5 items-start">
          <div className="space-y-4 lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-4">
              <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
                Active Service
              </label>
              <select
                value={selectedService?._id || ""}
                onChange={(e) => {
                  const s = services.find((sv) => sv._id === e.target.value);
                  setSelectedService(s);
                }}
                className="w-full h-10 px-3 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
              >
                {services.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.serviceType})
                  </option>
                ))}
              </select>
            </div>
            <AIProfileScoreCard
              serviceType={selectedService.serviceType}
              serviceId={selectedService._id}
            />
          </div>

          <div className="min-w-0">
            {activeTab === "packages" && (
              <AIPackageBuilder
                serviceType={selectedService.serviceType}
                serviceData={selectedService}
                serviceId={selectedService._id}
                onApplyPackages={(pkgs) =>
                  toast.success(`${pkgs.length} packages applied to service`)
                }
              />
            )}
            {activeTab === "documents" && (
              <AIDocumentReader
                onExtracted={() =>
                  toast.success("Document data extracted — apply from Profile tab")
                }
              />
            )}
            {activeTab === "verification" && (
              <AIBusinessVerifier vendor={vendor} />
            )}
          </div>
        </div>
      )}

      {/* Full-width tabs */}
      {activeTab === "overview" && (
        <OverviewGrid onOpen={setActiveTab} />
      )}

      {activeTab === "leads" && <LeadScoreBoard />}

      {activeTab === "autoreply" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          <AutoReplySettings />
          <LeadScoreBoard />
        </div>
      )}

      {activeTab === "calendar" && (
        <CalendarBlockManager venues={venues} />
      )}
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────────────────── */
function Header({ toolCount, serviceCount }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-pink-500 p-6 sm:p-8 text-white shadow-lg shadow-purple-600/20">
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div className="flex items-start gap-4 min-w-0">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-inset ring-white/25 backdrop-blur-sm">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
              AI growth toolkit
            </span>
            <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">VendorLift</h1>
            <p className="text-sm text-white/85 mt-1.5 max-w-2xl leading-relaxed">
              Your AI-powered growth toolkit — score leads, build packages, automate replies & stay verified
            </p>
          </div>
        </div>
        {toolCount !== undefined && (
          <div className="flex gap-3 flex-shrink-0">
            {[
              { value: toolCount, label: "AI tools" },
              { value: serviceCount, label: serviceCount === 1 ? "Service" : "Services" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl bg-white/15 ring-1 ring-inset ring-white/20 px-4 py-2.5 text-center backdrop-blur-sm">
                <p className="font-['Manrope'] text-xl font-extrabold tabular-nums leading-none">{stat.value}</p>
                <p className="mt-1 text-[11px] font-medium text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Overview Grid — the landing view ────────────────────────────────────── */
function OverviewGrid({ onOpen }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {FEATURES.map(({ key, icon: Icon, label, desc }, i) => (
          <button
            key={key}
            onClick={() => onOpen(key)}
            className="group relative overflow-hidden bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6 text-left hover:border-purple-200 hover:shadow-xl hover:shadow-purple-900/5 hover:-translate-y-0.5 transition-all duration-200"
          >
            <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-purple-100/0 blur-2xl transition-colors duration-300 group-hover:bg-purple-100/80" />
            <div className="relative flex items-start justify-between">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-md shadow-purple-600/20 group-hover:scale-105 transition-transform">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <span className="font-['Manrope'] text-xs font-bold text-gray-300 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <h3 className="relative font-['Manrope'] text-base font-bold text-gray-900 mt-4">{label}</h3>
            <p className="relative text-sm text-gray-500 leading-relaxed mt-1">{desc}</p>
            <span className="relative mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              Open tool
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </button>
        ))}
      </div>

      {/* How it works */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-7">
        <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-5">How it works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { step: "1", title: "Add services", text: "List your venues, catering, photography or decor services" },
            { step: "2", title: "Let AI analyse", text: "AI scores your leads, generates packages & verifies your documents" },
            { step: "3", title: "Grow faster", text: "Auto-reply keeps leads warm, calendar blocks prevent double-bookings" },
          ].map(({ step, title, text }, i, arr) => (
            <div key={step} className="relative">
              {i < arr.length - 1 && (
                <span className="hidden sm:block absolute top-5 left-14 right-0 h-px -mr-6 bg-gradient-to-r from-purple-200 to-transparent" />
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 font-['Manrope'] text-sm font-bold text-white shadow-md shadow-purple-600/20">
                {step}
              </div>
              <p className="text-sm font-bold text-gray-900 mt-3">{title}</p>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
